import {
  RotateCcw,
  Pause,
  Receipt,
  Percent,
  DollarSign,
  Gift,
  Printer,
  Settings,
  Calculator,
  Loader2,
  Undo2,
  LogIn,
  LogOut,
  UserPlus,
  Truck,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { usePOSStore } from '@/stores/posStore'
import { useCartStore } from '@/stores/cartStore'
import { useUIStore } from '@/stores/uiStore'
import { useOpenCashDrawer, usePrintReceipt } from '@/api/payments'
import { useCreateRefund, useVoidReceipt } from '@/api/receipts'
import { useHoldCurrentReceipt, useSelectCustomer, useCloseEcomReceipt } from '@/hooks'
import { fetchProductsForRefundItems, seedRefundPreview } from '@/lib/refundBootstrap'
import { extractTaxes } from '@/lib/utils'
import { setDeliveryMode } from '@/lib/deliveryMode'
import { SettingsModal } from '@/components/settings'
import { ManagerApprovalDialog } from './ManagerApprovalDialog'
import { checkManagerAccess } from '@/api/auth'
import { v2ErrorCode, v2ErrorText } from '@/api/v2'
import { PIN_THROTTLED, PIN_THROTTLED_TEXT, PinThrottledError } from '@/lib/discountLimit'
import { isTillOffline, withApprovalTimeout } from '@/hooks/useDiscountApproval'
import { RefundModal } from '@/components/refund/RefundModal'
// CashInModal and CashOutModal both live in POSLayout (routes/pos.tsx).
// FunctionsTab triggers them via uiStore.openCashInModal/openCashOutModal.
import { AddCustomerModal } from '@/components/customers/AddCustomerModal'
import type { Customer, ViewMode } from '@/types'

interface POSFunction {
  id: string
  name: string
  icon: typeof Receipt
  description: string
  color: string
  requiresManager?: boolean
}

const functionGroups = [
  {
    title: 'Receipt',
    functions: [
      { id: 'void', name: 'Void Receipt', icon: RotateCcw, description: 'Cancel current transaction', color: 'bg-red-500' },
      { id: 'hold', name: 'Hold Receipt', icon: Pause, description: 'Save receipt for later', color: 'bg-amber-500' },
      { id: 'recall', name: 'Recall Hold', icon: RotateCcw, description: 'Resume held receipt', color: 'bg-green-500' },
      { id: 'reprint', name: 'Reprint Receipt', icon: Printer, description: 'Print last receipt', color: 'bg-gray-500' },
      { id: 'refund', name: 'Refund/Return', icon: Undo2, description: 'Process refund or return', color: 'bg-orange-500' },
      { id: 'addcustomer', name: 'Add Customer', icon: UserPlus, description: 'Create new customer', color: 'bg-blue-500' },
    ],
  },
  {
    title: 'Discounts & Payments',
    functions: [
      { id: 'discount', name: 'Apply Discount', icon: Percent, description: 'Add discount to receipt', color: 'bg-green-500' },
      { id: 'giftcard', name: 'Gift Card', icon: Gift, description: 'Sell or redeem gift card', color: 'bg-purple-500' },
      { id: 'payout', name: 'Payout', icon: DollarSign, description: 'Cash payout', color: 'bg-emerald-500' },
      { id: 'nodrawer', name: 'No Sale', icon: Calculator, description: 'Open cash drawer', color: 'bg-gray-500' },
    ],
  },
  {
    title: 'Reports & Staff',
    functions: [
      { id: 'cashin', name: 'Cash In', icon: LogIn, description: 'Start shift & count cash', color: 'bg-green-500' },
      { id: 'cashout', name: 'Cash Out', icon: LogOut, description: 'End shift & count cash', color: 'bg-red-500' },
      { id: 'settings', name: 'Settings', icon: Settings, description: 'POS configuration', color: 'bg-gray-700', requiresManager: true },
      { id: 'delivery-mode', name: 'Delivery Mode', icon: Truck, description: 'Switch to the driver delivery view', color: 'bg-blue-600' },
    ],
  },
]

export function FunctionsTab() {
  const { viewMode, currentReceiptId, lastCompletedReceiptId, isRefundMode, clearReceipt, cart } = usePOSStore()
  const { showToast, openModal, openCashInModal, openCashOutModal, showConfirm } = useUIStore()
  const navigate = useNavigate()

  // Cash In / Cash Out / Payout each take the register out of the
  // sales flow (clock-in, clock-out, paid-out). Running any of them
  // mid-sale would either invalidate the in-progress cart or commit
  // a drawer count that doesn't reflect the as-yet-uncharged total.
  // Block all three behind a one-button warning; cashier must
  // complete, hold, or void the cart first.
  const blockIfPendingTransaction = (proceed: () => void) => {
    if (cart.length > 0) {
      showConfirm({
        title: 'Pending Transaction',
        message:
          'Cannot complete this action while a transaction is pending. Please complete, hold, or void the current sale first.',
        confirmLabel: 'OK',
        cancelLabel: null,
        variant: 'danger',
        onConfirm: () => {},
      })
      return
    }
    proceed()
  }
  const [showSettings, setShowSettings] = useState(false)
  // Manager-PIN gate for the Settings tile. Verified server-side by
  // check-manager-access (the PIN used to be a constant in the bundle).
  // No `permission` is sent: the portal has no POS-settings permission, so
  // it stays on the BE's manager/special-role check. Payout is deliberately
  // NOT gated: it never was, and the BE pay_out endpoint doesn't check one.
  const [showSettingsPin, setShowSettingsPin] = useState(false)
  // Whether the PIN dialog is still open. Read after the PIN check resolves
  // so a dialog closed mid-check opens nothing.
  const showSettingsPinRef = useRef(showSettingsPin)
  useEffect(() => {
    showSettingsPinRef.current = showSettingsPin
  }, [showSettingsPin])
  const [showRefundModal, setShowRefundModal] = useState(false)
  // CashInModal AND CashOutModal are mounted once in POSLayout and
  // driven via uiStore (openCashInModal / openCashOutModal). CashIn
  // also auto-prompts on POS entry from the layout effect; both are
  // triggered manually from this tab's buttons.
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false)

  const openCashDrawer = useOpenCashDrawer()
  const { holdCurrent, isPending: isHolding } = useHoldCurrentReceipt()
  const printReceipt = usePrintReceipt()
  const createRefund = useCreateRefund()
  const voidReceipt = useVoidReceipt()
  const closeEcomReceipt = useCloseEcomReceipt()
  const selectCustomer = useSelectCustomer()
  const queryClient = useQueryClient()

  const handleNewCustomerSuccess = (customer: Customer) => {
    // Close add-customer modal only on successful attach. Mirrors the
    // pattern in LeftSidebar / CustomersTab: a blocked customer change
    // (partial-payment lock) keeps the modal visible so the cashier
    // sees the warning toast in context.
    selectCustomer.mutate(customer, {
      onSuccess: () => setShowAddCustomerModal(false),
    })
  }

  const loadingIds = new Set<string>()
  if (isHolding) loadingIds.add('hold')
  if (openCashDrawer.isPending) loadingIds.add('nodrawer')
  if (printReceipt.isPending) loadingIds.add('reprint')

  const handleRefundReceiptSelected = async (receipt: any) => {
    try {
      // is_return: true — this is the cashier-driven refund flow.
      // Without this flag the backend's copy_with_refund creates a
      // plain copy of the source receipt with no `is_return` marker,
      // so it shows up in the Orders tab as a regular "Paid" sale
      // instead of a refund. Defaults to true on the hook; explicit
      // here so the intent is obvious at the call site.
      const refund = await createRefund.mutateAsync({
        sourceReceiptId: receipt.id,
        isReturn: true,
      })

      // Split-refund guards (refund-integrity). The BE (portal branch
      // waio-36-birchmount-refunds) seeds only REMAINING quantities and
      // marks its contract via jt_id/remaining on source_payments.
      //  1. Empty seed = everything already returned -> nothing to do.
      //  2. Previously-refunded receipt + NO contract markers = old BE
      //     that reseeds the FULL cart -> proceeding would double-refund;
      //     bail (the abandoned pending return auto-voids on next entry).
      const wasPreviouslyRefunded = Boolean(
        receipt.return_number || receipt.return_total || receipt.return_receipt_id,
      )
      const hasNewBeContract = (refund.source_payments ?? []).some(
        (sp: any) => sp?.jt_id != null || sp?.remaining != null,
      )
      if ((refund.items ?? []).length === 0) {
        showToast({
          type: 'info',
          title: 'Nothing Left to Refund',
          message: 'All items on this receipt have already been returned.',
        })
        // Don't strand the just-created return receipt (it would sit in
        // Orders as a live New return -- and on an old BE, a full-cart
        // completion path for the exact double-refund this guard stops).
        // Best-effort: already_deleted (409) is benign.
        if (refund.id) {
          try { await voidReceipt.mutateAsync({ receiptId: refund.id }) } catch { /* benign */ }
        }
        return
      }
      if (wasPreviouslyRefunded && !hasNewBeContract) {
        showToast({
          type: 'warning',
          title: 'Refund Blocked',
          message: 'This receipt was already refunded and the server does '
            + 'not support split refunds yet. Contact support.',
        })
        // Don't strand the just-created return receipt (it would sit in
        // Orders as a live New return -- and on an old BE, a full-cart
        // completion path for the exact double-refund this guard stops).
        // Best-effort: already_deleted (409) is benign.
        if (refund.id) {
          try { await voidReceipt.mutateAsync({ receiptId: refund.id }) } catch { /* benign */ }
        }
        return
      }

      const { enterRefundMode, loadRefundItems, setServerTotals } = usePOSStore.getState()

      // Enter refund mode first (clears cart, sets receipt ID, stashes
      // source payments so the checkout modal can gate refund methods).
      enterRefundMode(refund, refund.source_payments ?? null)

      // Set server totals from the refund response
      if (refund.totals) {
        const t = refund.totals as Record<string, unknown> & {
          subtotal?: number | string
          deposit_amount?: number | string
          recycling_amount?: number | string
          total?: number | string
        }
        const subtotal = Number(t.subtotal ?? 0)
        const taxes = extractTaxes(t)
        const deposit = Number(t.deposit_amount ?? t.recycling_amount ?? 0)
        const total = Number(t.total ?? 0)
        const amountToPay = Number(refund.amount_to_pay ?? total)
        setServerTotals({ subtotal, taxes, deposit, total, amountToPay })
      } else {
        setServerTotals(null)
      }

      // Also seed cartStore.preview so the CFD (which broadcasts
      // cart.preview.totals, not posStore.serverTotals) shows real
      // tax / total instead of falling back to lines-only subtotal.
      seedRefundPreview(refund)

      // Pull rich catalog data for each refund line so cart rows render
      // with image, brand, strain, THC%, tags, IOH — same as on-hold
      // recall. /copy-with-refund returns lightweight items missing
      // these fields. Cache hits are free; misses fan out parallel
      // /objects/products/{id} fetches.
      const productMap = refund.items && refund.items.length > 0
        ? await fetchProductsForRefundItems(
            refund.items as unknown as Parameters<typeof fetchProductsForRefundItems>[0],
            queryClient,
          )
        : new Map()

      // Load items into cart (this will trigger sync initialization)
      if (refund.items && refund.items.length > 0) {
        loadRefundItems(refund.items, productMap)
      }
      
      // Syncing will be set to false by the sync hook after initialization
      
      showToast({
        type: 'success',
        title: 'Refund Mode',
        message: `Loaded ${refund.items?.length || 0} refund items`
      })
    } catch (error) {
      console.error('Failed to create refund receipt:', error)
      showToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to create refund receipt'
      })
    }
  }

  const handleFunctionClick = (func: POSFunction) => {
    if (loadingIds.has(func.id)) return

    switch (func.id) {
      case 'void': {
        const { cart: currentCart, selectedCustomer } = usePOSStore.getState()
        // "Nothing to void" only when nothing is set anywhere — including
        // a selected customer. See LeftSidebar.handleVoid for the same
        // guard. Direct-clear semantics from void_flow.md.
        if (currentCart.length === 0 && !currentReceiptId && !selectedCustomer) {
          showToast({ type: 'warning', title: 'Nothing to Void', message: 'Cart is empty.' })
          break
        }

        // Refund-in-progress: discard the local refund cart, no backend void.
        if (isRefundMode) {
          clearReceipt()
          showToast({ type: 'success', title: 'Refund Cancelled', message: 'Refund has been cancelled.' })
          break
        }

        // Loaded ecom/delivery order: CLOSE (revert to its pre-load status +
        // clear) rather than cancel — the clerk may just be viewing it. Real
        // cancellation stays on the OrderDetailModal "Cancel Order" button.
        if (usePOSStore.getState().loadedEcomMeta) {
          ;(async () => {
            try {
              await closeEcomReceipt.close()
              showToast({ type: 'success', title: 'Order Closed', message: 'Order returned to the list. It was not cancelled.' })
            } catch (err) {
              showToast({
                type: 'error',
                title: 'Could not close order',
                message: err instanceof Error ? err.message : undefined,
              })
            }
          })()
          break
        }

        ;(async () => {
          if (currentReceiptId) {
            // Pre-flight: fully-paid receipts can't be voided BE-side
            // ("Cannot void a receipt with payments"). When we already
            // know the balance is zero, skip the round-trip and detach
            // locally — escape hatch for the post-payment stuck state.
            // See LeftSidebar.handleVoid for full context.
            const { serverTotals } = usePOSStore.getState()
            const amountToPay = serverTotals?.amountToPay
            if (amountToPay !== undefined && amountToPay !== null && Math.abs(amountToPay) <= 0.005) {
              clearReceipt()
              // Match the BE-failure branch wording below — same root
              // cause should read identically to the cashier whether we
              // caught it pre-flight or via the BE refusal.
              showToast({
                type: 'warning',
                title: 'Receipt Already Closed',
                message: "Paid receipts can't be voided. Cart cleared — use Refund/Return for paid orders.",
              })
              return
            }
            try {
              await voidReceipt.mutateAsync({ receiptId: currentReceiptId })
            } catch (err: unknown) {
              const axiosErr = err as { response?: { status?: number; data?: { code?: string; error?: string } } }
              const code = axiosErr?.response?.data?.code
              const errMsg = (axiosErr?.response?.data?.error || (err instanceof Error ? err.message : '')).toLowerCase()

              // Terminal — see LeftSidebar.handleVoid for full context
              // on why HTTP 500 also counts (v2 backend currently 500s
              // on paid-receipt voids instead of returning a structured
              // 409) and why 'with payments' / 'cancel any payments'
              // are treated terminal (v2_operation_failed wrapper for
              // paid-receipt voids). clearReceipt() is local-only.
              const status = axiosErr?.response?.status
              const isTerminal =
                status === 409
                || status === 500
                || code === 'cannot_cancel_paid'
                || code === 'already_terminal'
                || code === 'payment_in_progress'
                || errMsg.includes('is locked')
                || errMsg.includes('cannot be modified')
                || errMsg.includes('already paid')
                || errMsg.includes('with payments')
                || errMsg.includes('cancel any payments')

              if (isTerminal) {
                clearReceipt()
                showToast({
                  type: 'warning',
                  title: 'Receipt Already Closed',
                  message:
                    code === 'cannot_cancel_paid' || errMsg.includes('already paid') || errMsg.includes('with payments')
                      ? "Paid receipts can't be voided. Cart cleared — use Refund/Return for paid orders."
                      : 'Receipt is closed on the backend. Cart cleared so you can start fresh.',
                })
                return
              }

              const msg = axiosErr?.response?.data?.error || (err instanceof Error ? err.message : 'Void failed.')
              showToast({ type: 'error', title: 'Void Failed', message: msg })
              return
            }
          }
          clearReceipt()
          showToast({ type: 'success', title: 'Receipt Voided', message: 'Transaction has been cancelled.' })
        })()
        break
      }

      case 'hold':
        holdCurrent()
        break

      case 'recall':
        // Recall Hold loads a previously-held receipt into the cart;
        // doing it on top of an in-progress sale would clobber the
        // current cart contents. Same pending-transaction gate.
        blockIfPendingTransaction(() => openModal('held-receipts'))
        break

      case 'reprint': {
        const receiptId = lastCompletedReceiptId
        if (!receiptId) {
          showToast({ type: 'warning', title: 'No Receipt', message: 'No completed receipt to reprint.' })
          break
        }
        printReceipt.mutate(
          { receiptId, isCopy: true },
          {
            onSuccess: () => showToast({ type: 'success', title: 'Receipt Sent', message: 'Reprint sent to printer.' }),
            onError: () => showToast({ type: 'error', title: 'Print Failed', message: 'Could not send receipt to printer.' }),
          }
        )
        break
      }

      case 'refund':
        // Refund/Return flips the register into refund mode and seeds
        // the cart from a source receipt's items -- it cannot coexist
        // with an active sale in the cart. Same pending-transaction
        // gate.
        blockIfPendingTransaction(() => setShowRefundModal(true))
        break

      case 'discount':
        openModal('discount')
        break

      case 'tax-exempt': {
        // Pure cartStore toggle. The bridge propagates exempt_taxes into
        // the next /cart/preview request and /cart/commit body, so the
        // BE re-prices the cart accordingly. No legacy receipt-side
        // /exempt-taxes call needed -- the exemption travels with the
        // commit, not as a side-channel mutation.
        const next = !useCartStore.getState().exempt_taxes
        useCartStore.getState().setExemptTaxes(next)
        showToast({
          type: 'success',
          title: next ? 'Tax Exempt: ON' : 'Tax Exempt: OFF',
          message: next
            ? 'Taxes will not apply to this transaction.'
            : 'Tax exemption removed.',
        })
        break
      }

      case 'nodrawer':
        openCashDrawer.mutate(undefined, {
          onSuccess: () => showToast({ type: 'success', title: 'Cash Drawer', message: 'Drawer opened.' }),
          onError: () => showToast({ type: 'error', title: 'Drawer Error', message: 'Could not open cash drawer.' }),
        })
        break

      case 'giftcard':
        openModal('gift-card-sell')
        break
      case 'payout':
        blockIfPendingTransaction(() => openModal('payout'))
        break
      case 'cashin':
        blockIfPendingTransaction(() => openCashInModal())
        break
      case 'cashout':
        blockIfPendingTransaction(() => openCashOutModal())
        break
      case 'addcustomer':
        setShowAddCustomerModal(true)
        break
      case 'settings':
        setShowSettingsPin(true)
        break
      case 'delivery-mode':
        setDeliveryMode(true)
        navigate({ to: '/driver' })
        break
      default:
        showToast({ type: 'info', title: func.name, message: 'Feature coming soon.' })
    }
  }

  return (
    <div className="flex-1 overflow-y-auto p-4">
      {functionGroups.map((group) => (
        <div key={group.title} className="mb-6">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            {group.title}
          </h3>
          <div className={viewMode === 'grid'
            ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3'
            : 'space-y-2'
          }>
            {group.functions.map((func) => (
              <FunctionCard
                key={func.id}
                func={func}
                viewMode={viewMode}
                isLoading={loadingIds.has(func.id)}
                onClick={() => handleFunctionClick(func)}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Manager PIN gate for Settings */}
      <ManagerApprovalDialog
        isOpen={showSettingsPin}
        onClose={() => setShowSettingsPin(false)}
        title="Manager PIN"
        subtitle="Enter a manager PIN to open Settings"
        submitLabel="Unlock"
        // The same list UI as a discount approval: one row, what it unlocks.
        items={[{ key: 'settings', title: 'Unlock Settings' }]}
        onSubmit={async (pin) => {
          const offlineText = 'Manager PIN check needs a connection. Try again once the till is back online.'
          // Called directly, not through a react-query mutation: an offline
          // mutation is PAUSED and resumed on reconnect, which would open
          // Settings later, unprompted. Fail fast instead.
          if (isTillOffline()) throw new Error(offlineText)
          try {
            // Bounded like every approval call (the dialog can't close
            // mid-check).
            await withApprovalTimeout((signal) => checkManagerAccess(pin, undefined, signal))
          } catch (err) {
            // 429 pin_throttled locks the dialog's submit for 30s.
            if (v2ErrorCode(err) === PIN_THROTTLED) throw new PinThrottledError(PIN_THROTTLED_TEXT)
            if (err instanceof Error && err.name === 'ApprovalTimeoutError') throw new Error(offlineText)
            throw new Error(v2ErrorText(err, 'Wrong PIN'))
          }
          if (!showSettingsPinRef.current) return
          setShowSettings(true)
        }}
      />

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />

      {/* Refund Modal */}
      <RefundModal
        open={showRefundModal}
        onOpenChange={setShowRefundModal}
        onReceiptSelected={handleRefundReceiptSelected}
      />

      {/* Cash IN + Cash OUT modals both live in POSLayout (routes/pos.tsx). */}

      {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <AddCustomerModal
          onClose={() => setShowAddCustomerModal(false)}
          onSuccess={handleNewCustomerSuccess}
        />
      )}
    </div>
  )
}

interface FunctionCardProps {
  func: POSFunction
  viewMode: ViewMode
  isLoading: boolean
  onClick: () => void
}

function FunctionCard({ func, viewMode, isLoading, onClick }: FunctionCardProps) {
  const Icon = func.icon

  if (viewMode === 'list') {
    return (
      <button
        onClick={onClick}
        disabled={isLoading}
        className="w-full p-3 bg-white rounded-xl flex items-center gap-4 hover:ring-2 hover:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all text-left"
      >
        <div className={`w-10 h-10 ${func.color} rounded-xl flex items-center justify-center text-white`}>
          {isLoading ? <Loader2 size={20} className="animate-spin" /> : <Icon size={20} />}
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-gray-900">{func.name}</h4>
          <p className="text-sm text-gray-500">{func.description}</p>
        </div>
        {func.requiresManager && (
          <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
            Manager
          </span>
        )}
      </button>
    )
  }

  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      className="p-4 bg-white rounded-xl flex flex-col items-center hover:ring-2 hover:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all relative"
    >
      {func.requiresManager && (
        <span className="absolute top-2 right-2 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full">
          Manager
        </span>
      )}
      <div className={`w-12 h-12 ${func.color} rounded-xl flex items-center justify-center text-white mb-2`}>
        {isLoading ? <Loader2 size={24} className="animate-spin" /> : <Icon size={24} />}
      </div>
      <h4 className="font-medium text-gray-900 text-sm text-center">{func.name}</h4>
      <p className="text-xs text-gray-500 text-center mt-1">{func.description}</p>
    </button>
  )
}
